import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Check, Wallet, X } from 'lucide-react';
import { PageLoadingSpinner } from '@kioskgaming/page-loading';
import { convertBalanceToCredits, getProviderFeeRate, listZeroxStaticWallets } from '../../services/api';
import { formatDepositFeeBreakdown } from '../../utils/netWithdrawalFee';
import { filterUsdDepositStaticWallets, zeroxMethodIdFromStaticWallet } from '../../utils/zeroxMethodId';
import type { ZeroxStaticWallet } from '../../types';

export type PurchaseTab = 'crypto' | 'balance';

export interface CreditPurchaseModalProps {
  open: boolean;
  onClose: () => void;
  costRate?: number;
  onOrdered?: () => void;
  creditBalance?: number | null;
  creditBalanceLoading?: boolean;
  /** USD online balance from dashboard (UsdWallet.balanceNet) */
  onlineBalanceNet?: number | null;
  onlineBalanceLoading?: boolean;
  onBalancesRefresh?: () => void | Promise<void>;
  /** Tab shown when the modal opens (set by trigger: Deposit to usd wallet vs Purchase credits). */
  initialTab?: PurchaseTab;
  /** CDN cost rate (0–1): credits = USD amount ÷ rate (same as crypto purchase). */
  conversionCostRate?: number | null;
}

function formatUsd(n: number | null | undefined): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function roundCreditsPreview(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function resolveStaticWalletNetwork(wallet: ZeroxStaticWallet | null | undefined): string | null {
  if (!wallet) return null;
  const raw = wallet.raw as any;
  const rawCandidates = [
    raw?.network,
    raw?.Network,
    raw?.networkName,
    raw?.NetworkName,
    raw?.chain,
    raw?.Chain,
    raw?.blockchain,
    raw?.Blockchain,
    raw?.protocol,
    raw?.Protocol,
    raw?.raw?.network,
    raw?.raw?.Network,
  ];
  const fromRaw = rawCandidates.find((value) => typeof value === 'string' && value.trim() !== '')?.trim() ?? null;
  if (fromRaw) return fromRaw;

  const cur = String(wallet.currency ?? '').trim();
  const m = cur.match(/\(([^)]+)\)/);
  if (m?.[1]?.trim()) return m[1].trim();

  const ticker = cur.split('(')[0].trim().split(/\s+/)[0].toUpperCase();
  if (ticker === 'BTC') return 'Bitcoin';
  if (ticker === 'ETH') return 'Ethereum';
  return null;
}

export default function CreditPurchaseModal({
  open,
  onClose,
  onlineBalanceNet,
  onlineBalanceLoading,
  onBalancesRefresh,
  initialTab = 'crypto',
  conversionCostRate,
}: CreditPurchaseModalProps) {
  const [activeTab, setActiveTab] = useState<PurchaseTab>('crypto');
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState<ZeroxStaticWallet[]>([]);
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [feeGatewayRate, setFeeGatewayRate] = useState<number | null>(null);
  const [feeSystemRate, setFeeSystemRate] = useState<number | null>(null);
  const [feeNetworkType, setFeeNetworkType] = useState<'none' | 'percent' | 'fixed'>('none');
  const [feeNetworkRate, setFeeNetworkRate] = useState<number | null>(null);
  const [feeNetworkAmount, setFeeNetworkAmount] = useState<number | null>(null);
  const [feeMatchedMethodId, setFeeMatchedMethodId] = useState<string | null>(null);
  const [convertAmount, setConvertAmount] = useState('');
  const [converting, setConverting] = useState(false);

  const availableUsd = useMemo(() => {
    const v = Number(onlineBalanceNet);
    return Number.isFinite(v) ? v : 0;
  }, [onlineBalanceNet]);

  const selectedWallet = useMemo(() => {
    if (!wallets.length) return null;
    if (selectedWalletId) return wallets.find((w) => w.id === selectedWalletId) ?? null;
    return wallets[0] ?? null;
  }, [wallets, selectedWalletId]);

  const selectedWalletNetwork = useMemo(() => {
    return resolveStaticWalletNetwork(selectedWallet);
  }, [selectedWallet]);

  const selectedWalletMethodId = useMemo(
    () => zeroxMethodIdFromStaticWallet(selectedWallet),
    [selectedWallet]
  );

  const parsedConvert = useMemo(() => {
    const t = String(convertAmount).trim().replace(/,/g, '');
    const n = Number.parseFloat(t);
    return Number.isFinite(n) ? n : NaN;
  }, [convertAmount]);

  const costRateNum = Number(conversionCostRate);
  const costRateOk = Number.isFinite(costRateNum) && costRateNum > 0;

  const estimatedCredits = useMemo(() => {
    if (!costRateOk || !Number.isFinite(parsedConvert) || parsedConvert <= 0) return NaN;
    return Math.round((parsedConvert / costRateNum) * 1e8) / 1e8;
  }, [parsedConvert, costRateNum, costRateOk]);

  const feeBreakdown = useMemo(
    () =>
      formatDepositFeeBreakdown({
        gatewayFeeRate: feeGatewayRate,
        systemFeeRate: feeSystemRate,
        networkFeeType: feeNetworkType,
        networkFeeRate: feeNetworkRate,
        networkFeeAmount: feeNetworkAmount,
      }),
    [feeGatewayRate, feeSystemRate, feeNetworkType, feeNetworkRate, feeNetworkAmount]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab ?? 'crypto');
  }, [open, initialTab]);

  useEffect(() => {
    if (open) return;
    setLoading(false);
    setWallets([]);
    setSelectedWalletId(null);
    setQrDataUrl(null);
    setFeeGatewayRate(null);
    setFeeSystemRate(null);
    setFeeNetworkType('none');
    setFeeNetworkRate(null);
    setFeeNetworkAmount(null);
    setFeeMatchedMethodId(null);
    setConvertAmount('');
    setConverting(false);
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    async function loadWallets() {
      if (!open || activeTab !== 'crypto') return;
      setLoading(true);
      try {
        const res = await listZeroxStaticWallets();
        const items = res.data?.items ?? [];
        const normalized = filterUsdDepositStaticWallets(
          Array.isArray(items) ? (items as ZeroxStaticWallet[]) : []
        );
        if (cancelled) return;
        setWallets(normalized);
        setSelectedWalletId(normalized.length === 1 ? (normalized[0]?.id ?? null) : null);
      } catch (e: any) {
        if (!cancelled) toast.error(e?.response?.data?.message || 'Could not load static wallets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadWallets();
    return () => {
      cancelled = true;
    };
  }, [open, activeTab]);

  useEffect(() => {
    let cancelled = false;
    async function buildQr() {
      if (!selectedWallet?.address || activeTab !== 'crypto') return;
      try {
        const QRCode = await import('qrcode');
        const url = await QRCode.toDataURL(selectedWallet.address, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 240,
        });
        if (!cancelled) setQrDataUrl(url);
      } catch {
        if (!cancelled) setQrDataUrl(null);
      }
    }
    setQrDataUrl(null);
    void buildQr();
    return () => {
      cancelled = true;
    };
  }, [selectedWallet?.address, activeTab]);

  useEffect(() => {
    let cancelled = false;
    async function loadFee() {
      if (!open || activeTab !== 'crypto' || !selectedWallet) return;
      try {
        const res = await getProviderFeeRate({
          provider: 'zeroxprocessing',
          operation: 'deposit',
          methodId: selectedWalletMethodId,
        });
        const gatewayRate = Number((res as any)?.data?.gatewayFeeRate ?? 0);
        const systemRate = Number((res as any)?.data?.systemFeeRate ?? 0);
        const matched = (res as any)?.data?.matchedMethodId ?? null;
        const networkType = String((res as any)?.data?.networkFeeType || 'none').toLowerCase();
        const networkRate = Number((res as any)?.data?.networkFeeRate ?? 0);
        const networkAmount = Number((res as any)?.data?.networkFeeAmount ?? 0);
        if (!cancelled) {
          setFeeGatewayRate(Number.isFinite(gatewayRate) ? gatewayRate : 0);
          setFeeSystemRate(Number.isFinite(systemRate) ? systemRate : 0);
          setFeeNetworkType(networkType === 'percent' || networkType === 'fixed' ? networkType : 'none');
          setFeeNetworkRate(Number.isFinite(networkRate) ? networkRate : 0);
          setFeeNetworkAmount(Number.isFinite(networkAmount) ? networkAmount : 0);
          setFeeMatchedMethodId(matched ? String(matched) : null);
        }
      } catch {
        if (!cancelled) {
          setFeeGatewayRate(null);
          setFeeSystemRate(null);
          setFeeNetworkType('none');
          setFeeNetworkRate(null);
          setFeeNetworkAmount(null);
          setFeeMatchedMethodId(null);
        }
      }
    }
    void loadFee();
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, selectedWallet?.id, selectedWalletMethodId, selectedWallet]);

  async function handleConvertToCredits() {
    const amt = Math.round(parsedConvert * 100) / 100;
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (amt > availableUsd + 1e-9) {
      toast.error('Insufficient USD wallet balance');
      return;
    }
    setConverting(true);
    try {
      const idem =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      const res = await convertBalanceToCredits(amt, idem);
      if (res.success && res.data) {
        toast.success(
          `Converted ${formatUsd(res.data.usd_deducted)} → ${roundCreditsPreview(res.data.credits_added)} credits (rate ${Number(res.data.cost_rate_applied).toLocaleString(undefined, { maximumFractionDigits: 4 })})`
        );
        await onBalancesRefresh?.();
        onClose();
      } else {
        toast.error(res.message || 'Conversion failed');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  }

  if (!open) return null;

  const subtitle =
    activeTab === 'crypto'
      ? 'Pay with crypto via static wallet (0xProcessing).'
      : 'Use USD wallet balance. Credits = USD ÷ your cost rate; hierarchy funds credits (agent → super agent). No extra platform fee.';

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        aria-label="Close dialog"
        onClick={onClose}
      />

      <div className="absolute inset-0 z-20 flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div
          className="relative w-full max-w-2xl rounded-2xl border border-white/15 bg-[rgba(15,23,42,0.98)] p-4 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-black uppercase tracking-wide text-white/60">Deposit to usd wallet</p>
              <h3 className="mt-1 text-base font-black text-white">
                {activeTab === 'crypto' ? 'Pay with Crypto' : 'Purchase credits'}
              </h3>
              <p className="mt-1 text-xs text-white/60">{subtitle}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white/80 hover:bg-white/10"
            >
              <X className="h-4 w-4" aria-hidden />
              Close
            </button>
          </div>

          <div className="mt-4 flex gap-2 rounded-xl border border-white/10 bg-white/5 p-1">
            <button
              type="button"
              onClick={() => setActiveTab('crypto')}
              className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wide transition ${activeTab === 'crypto' ? 'bg-amber-500/25 text-amber-100' : 'text-white/55 hover:bg-white/10'
                }`}
            >
              Pay with Crypto
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('balance')}
              className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wide transition ${activeTab === 'balance' ? 'bg-emerald-500/25 text-emerald-100' : 'text-white/55 hover:bg-white/10'
                }`}
            >
              Purchase credits
            </button>
          </div>

          {activeTab === 'balance' ? (
            <div className="mt-5 space-y-4 rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-sm text-white/90">
                <span className="text-white/50">Available: </span>
                {onlineBalanceLoading ? (
                  <span className="text-white/45">Loading…</span>
                ) : (
                  <span className="font-semibold tabular-nums text-emerald-300">{formatUsd(availableUsd)} available</span>
                )}
              </p>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-white/50" htmlFor="convert-usd-amount">
                  Amount (USD)
                </label>
                <div className="mt-2 flex gap-2">
                  <input
                    id="convert-usd-amount"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={convertAmount}
                    onChange={(e) => setConvertAmount(e.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-white/15 bg-white/5 px-3 py-2 font-mono text-sm text-white placeholder:text-white/35 focus:border-amber-400/50 focus:outline-none"
                    placeholder="0.00"
                  />
                  <button
                    type="button"
                    onClick={() => setConvertAmount(availableUsd.toFixed(2))}
                    disabled={onlineBalanceLoading || availableUsd <= 0}
                    className="shrink-0 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white/80 hover:bg-white/10 disabled:opacity-40"
                  >
                    Max
                  </button>
                </div>
              </div>
              {!costRateOk ? (
                <p className="text-sm text-amber-200/90">
                  Cost rate is missing or zero on your account. Ask admin to set cost rate before converting.
                </p>
              ) : null}
              <p className="text-sm text-white/70">
                {Number.isFinite(parsedConvert) && parsedConvert > 0 && costRateOk ? (
                  <>
                    Convert {formatUsd(parsedConvert)} →{' '}
                    <span className="font-semibold text-white">{roundCreditsPreview(estimatedCredits)}</span> credits
                    <span className="text-white/45">
                      {' '}
                      (USD ÷ {costRateNum.toLocaleString(undefined, { maximumFractionDigits: 4 })}; upstream credits)
                    </span>
                  </>
                ) : Number.isFinite(parsedConvert) && parsedConvert > 0 && !costRateOk ? (
                  <>Cannot estimate credits until cost rate is configured.</>
                ) : (
                  <>Enter an amount to see the summary.</>
                )}
              </p>
              <button
                type="button"
                onClick={() => void handleConvertToCredits()}
                disabled={
                  converting ||
                  onlineBalanceLoading ||
                  !costRateOk ||
                  !Number.isFinite(parsedConvert) ||
                  parsedConvert <= 0 ||
                  parsedConvert > availableUsd + 1e-9
                }
                className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {converting ? 'Converting…' : 'Convert to Credits'}
              </button>
            </div>
          ) : loading ? (
            <div className="mt-5 flex items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-10 text-white/70">
              <PageLoadingSpinner size="sm" className="text-white/80" />
              <span className="ml-2 text-sm font-semibold">Loading wallet…</span>
            </div>
          ) : wallets.length === 0 ? (
            <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4 text-sm text-amber-100">
              No static wallet found yet. Refresh in a few seconds, or contact admin.
            </div>
          ) : (
            <>
              {wallets.length > 1 ? (
                <div className="mt-5">
                  <p className="text-xs font-bold uppercase tracking-wide text-white/50">Choose a wallet</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {wallets.map((w) => {
                      const cur = String(w.currency || '').toUpperCase();
                      const network = resolveStaticWalletNetwork(w);
                      const selected = selectedWallet?.id === w.id;
                      return (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => setSelectedWalletId(w.id)}
                          className={`relative rounded-xl border px-3 py-2 text-left transition ${selected ? 'border-amber-300 bg-amber-400/10' : 'border-white/10 bg-white/5 hover:bg-white/10'
                            }`}
                        >
                          {selected ? (
                            <span className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
                              <Check className="h-3 w-3" strokeWidth={3} />
                            </span>
                          ) : null}
                          <p className="text-sm font-black text-white">
                            {cur || '—'}
                            {network ? <span className="ml-1 text-[11px] font-bold text-white/60">({network})</span> : null}
                          </p>
                          <p className="mt-1 truncate text-[11px] font-mono text-white/60">{w.address}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {selectedWallet ? (
                <div className="mt-5 grid gap-3 sm:grid-cols-[260px_1fr] items-start">
                  <div className="rounded-xl border border-white/15 bg-white/5 p-3">
                    {qrDataUrl ? (
                      <img
                        src={qrDataUrl}
                        alt="Static wallet QR code"
                        className="mx-auto h-[240px] w-[240px] rounded-lg bg-white p-2"
                      />
                    ) : (
                      <div className="h-[240px] w-[240px] mx-auto flex items-center justify-center rounded-lg border border-white/10 text-white/60">
                        Generating QR…
                      </div>
                    )}
                    <p className="mt-2 text-[11px] text-white/45 text-center">
                      Currency:{' '}
                      <span className="font-semibold text-white/80">{String(selectedWallet.currency || '').toUpperCase()}</span>
                    </p>
                    <p className="mt-1 text-[11px] text-white/45 text-center">
                      Network: <span className="font-semibold text-white/80">{selectedWalletNetwork ?? '—'}</span>
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="rounded-xl border border-white/15 bg-white/5 p-3">
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-300">
                          <Wallet className="h-4 w-4" aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-white/50">Wallet address</p>
                          <p className="mt-1 break-all font-mono text-sm text-white/90">{selectedWallet.address}</p>
                          <p className="mt-3 text-xs font-medium text-white/50">Network</p>
                          <p className="mt-1 break-all font-mono text-sm text-white/90">{selectedWalletNetwork ?? '—'}</p>
                          {selectedWallet.tag ? (
                            <>
                              <p className="mt-3 text-xs font-medium text-white/50">Tag / Memo</p>
                              <p className="mt-1 break-all font-mono text-sm text-white/90">{selectedWallet.tag}</p>
                            </>
                          ) : null}
                          <p className="mt-3 text-xs text-white/45">Include transfer hash when contacting admin.</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-white/15 bg-white/5 p-3">
                      <p className="text-xs font-medium text-white/50">Fee breakdown</p>
                      <div className="mt-2 space-y-1 text-xs text-white/70">
                        <p className="flex justify-between gap-3">
                          <span>Gateway processing fee</span>
                          <span className="font-semibold text-white/90">{feeBreakdown.gatewayProcessingFee}</span>
                        </p>
                        <p className="flex justify-between gap-3">
                          <span>Network fee</span>
                          <span className="font-semibold text-white/90">{feeBreakdown.networkFee}</span>
                        </p>
                        <p className="flex justify-between gap-3">
                          <span>System fee</span>
                          <span className="font-semibold text-white/90">{feeBreakdown.systemFee}</span>
                        </p>
                        <p className="flex justify-between gap-3 border-t border-white/10 pt-2 font-semibold text-white/90">
                          <span>Total fee</span>
                          <span>{feeBreakdown.totalFee}</span>
                        </p>
                      </div>
                      {feeMatchedMethodId && feeMatchedMethodId !== selectedWalletMethodId ? (
                        <p className="mt-2 text-[10px] text-white/35">Fallback fee method: {feeMatchedMethodId}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
